import React, { useCallback, useEffect, useMemo } from 'react'
import { environment } from '@/shared/consts/environment'
import { BrowserContext } from '../../context'
import dynamic from 'next/dynamic'
import { global } from 'styled-jsx/css'
import { useUser } from 'containers/user/hooks/useUser'
import { useIsOwnProfile } from 'containers/profile/hooks/useProfile'
import { colorsV2 } from '@/styles/v2/vars/vars.colors'

const ProductFruits = dynamic(
  () => import('react-product-fruits').then((mod) => mod.ProductFruits),
  { ssr: false }
)

export const ProductFruitsWrapper = () => {
  const { user, isAuth } = useUser()
  const isOwnProfile = useIsOwnProfile()
  const { isMobile } = React.useContext(BrowserContext)

  let userId = user?.id

  // Identify users in all lower environments differently so we can test in each
  if (environment.env === 'development' || environment.env === 'staging') {
    userId = userId + `_${environment.env}`
  }

  const userInfo = useMemo(
    () => ({
      username: userId, // REQUIRED - any unique user identifier
      email: user?.email,
      firstname: user?.firstName,
      lastname: user?.lastName,
      props: {
        env: environment.env,
        profileV2: true,
      },
    }),
    [user, userId]
  )

  // listen for banner spacing event
  const bannerCallback = useCallback(() => {
    window.productFruits?.api?.announcementsV2?.listen('banner-spacing', () => {
      const body = document.querySelector('body')

      if (body) {
        const banner = document
          .querySelector('.productfruits--container')
          .shadowRoot.querySelector('.pfruits-banner') as HTMLDivElement

        if (!isMobile) {
          body.style.paddingTop = '82px'
          body.style.backgroundColor = colorsV2.background.light.primary
        }

        if (banner) {
          const bannerText = banner.querySelector(
            '.banner-txt'
          ) as HTMLDivElement
          const bannerAction = banner.querySelector(
            '.banner-action'
          ) as HTMLDivElement
          const bannerClose = banner.querySelector(
            '.banner-close'
          ) as HTMLDivElement

          // banner
          banner.style.display = 'flex'
          banner.style.alignItems = 'center'

          // action button
          if (bannerAction) {
            bannerAction.style.backgroundColor = colorsV2.background.dark.green
            bannerAction.style.color = 'white'
            bannerAction.style.padding = '6px 12px'
            bannerAction.style.borderRadius = '10px'
            bannerAction.style.fontSize = isMobile ? '12px' : '14px'
            bannerAction.style.transition = 'background-color 0.5s ease'

            bannerAction.onmouseover = function () {
              bannerAction.style.backgroundColor =
                colorsV2.background.dark.greenSecondary
            }

            bannerAction.onmouseout = function () {
              bannerAction.style.backgroundColor =
                colorsV2.background.dark.green
            }
          }

          // text
          if (bannerText && isMobile) {
            bannerText.style.fontSize = '12px'
          }

          // close button
          if (bannerClose) {
            bannerClose.style.right = '16px'
          }
        }
      }
    })
  }, [isMobile])

  // try to launch onboarding tour
  const tourCallback = useCallback(() => {
    try {
      const toursApi = window?.productFruits?.api?.tours
      const DESKTOP_TOUR = 44802
      const MOBILE_TOUR = 44803
      const tour =
        (isMobile && MOBILE_TOUR) ||
        (!DESKTOP_TOUR && MOBILE_TOUR) ||
        DESKTOP_TOUR
      const tourObj = toursApi?.getTours()?.find((t) => t.id === tour)
      const notFinished = tourObj?.userState !== 'finished'

      if (notFinished) {
        toursApi?.tryStartTour(tour)
      }
    } catch (error) {
      console.log(error)
      return
    }
  }, [isMobile])

  const isVisible = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return global.window && isAuth && user?.id
  }, [isAuth, user])

  useEffect(() => {
    window.productFruitsReady = () => {
      if (isVisible) {
        bannerCallback()
      }

      if (isVisible && isOwnProfile) {
        tourCallback()
      }
    }
  }, [isVisible, isOwnProfile, tourCallback, bannerCallback])

  if (!isVisible) return <></>

  return (
    // ProductFruits does not support typescript
    <ProductFruits
      workspaceCode="jdn6NnW3pWo7S0Dl"
      language="en"
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      user={userInfo}
    />
  )
}
